<template>
  <div>
    <!-- Top row -->
    <div v-if="showSettings" class="d-flex flex-nowrap gutter mb-3">
      <div class="left-col">
        <v-btn
          small
          @click="toggleShowSettings"
        >
          {{ hideSettingsText }}
        </v-btn>
      </div>

      <!-- Available fields -->
      <div class="flex-fill drag-area" :class="dragAreaClass">
        <div class="drag-area-title mb-3">
          {{ availableFieldsLabelText }}
        </div>
        <draggable
          v-model="internal.availableFieldKeys"
          class="d-flex flex-row gutter-sm drag-area-zone"
          group="fields"
          handle=".btn-draggable"
          @start="start"
          @end="end"
        >
          <template v-for="key in internal.availableFieldKeys">
            <div
              v-if="fieldsWithValues[key]"
              :key="key"
              class="field"
            >
              <field-label
                v-model="fieldValues[key]"
                :field="fieldsWithValues[key]"
                :select-all-text="selectAllText"
                :unselect-all-text="unselectAllText"
                variant="secondary"
              >
                <!-- pass down scoped slots -->
                <template v-for="(_, slot) of $scopedSlots" v-slot:[slot]="scope">
                  <slot :name="slot" v-bind="scope" />
                </template>
              </field-label>
            </div>
          </template>
        </draggable>
      </div>
    </div>

    <div v-else class="mb-3">
      <v-btn
        small
        @click="toggleShowSettings"
      >
        {{ showSettingsText }}
      </v-btn>
    </div>

    <div v-if="showSettings" class="d-flex gutter mb-3">
      <!-- Top left zone -->
      <div class="left-col" />

      <div class="flex-column col-8 pa-0">
        <v-switch
          v-model="internal.totalsMasterEnabled"
          :label="totalsMasterLabel"
          color="info"
          hide-details
          class="mb-3"
        />
        <v-switch
          v-model="isColumnValues"
          label="Режим вычисления по колонкам"
          color="info"
          hide-details
          class="mb-5"
        />

        <!-- Column fields -->
        <template v-if="isColumnValues">
          <div class="d-flex align-items-center flex-wrap column-values-select-row">
            <v-select
              v-model="currentKeyColumns"
              :items="valueColumns"
              label="Колонки значения"
              item-text="label"
              item-value="key"
              outlined
              multiple
              clearable
              class="flex-grow-1 column-values-select"
            />
            <v-tooltip
              v-if="internal.totalsMasterEnabled"
              bottom
            >
              <template #activator="{ on, attrs }">
                <span
                  class="d-inline-flex ml-2 pivot-totals-checkbox-tooltip-activator"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-checkbox
                    v-model="columnValuesFooterTotalsEnabled"
                    class="section-grand-total-checkbox mt-0 pt-0"
                    hide-details
                    dense
                  />
                </span>
              </template>
              <span>{{ columnValuesFooterTotalsTooltip }}</span>
            </v-tooltip>
          </div>
        </template>
        <template v-else>
          <div class="flex-fill drag-area border-primary" :class="dragAreaClass">
            <div class="drag-area-title drag-area-title--with-section-checkbox mb-3 d-flex flex-wrap">
              <span class="drag-area-title__label">{{ colsLabelText }}</span>
              <v-tooltip
                v-if="internal.totalsMasterEnabled && !isColumnValues"
                bottom
              >
                <template #activator="{ on, attrs }">
                  <span
                    class="d-inline-flex ml-2 pivot-totals-checkbox-tooltip-activator"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-checkbox
                      v-model="showGrandTotalRowEnabled"
                      class="section-grand-total-checkbox mt-0 pt-0"
                      hide-details
                      dense
                    />
                  </span>
                </template>
                <span>{{ grandTotalRowSectionTooltip }}</span>
              </v-tooltip>
            </div>
            <draggable
              v-model="internal.colFieldKeys"
              class="d-flex flex-row gutter-sm drag-area-zone"
              group="fields"
              handle=".btn-draggable"
              @start="start"
              @end="end"
            >
              <template v-for="key in internal.colFieldKeys">
                <div
                  v-if="fieldsWithValues[key]"
                  :key="key"
                  class="field"
                  :class="{ 'field-with-totals-toggle': internal.totalsMasterEnabled }"
                >
                  <v-tooltip
                    v-if="internal.totalsMasterEnabled"
                    bottom
                  >
                    <template #activator="{ on, attrs }">
                      <span
                        class="d-inline-flex pivot-totals-checkbox-tooltip-activator field-totals-checkbox-wrap"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-checkbox
                          v-model="internal.colFieldTotalsEnabled[key]"
                          class="field-totals-checkbox mt-0 pt-0"
                          hide-details
                          dense
                        />
                      </span>
                    </template>
                    <span>{{ dimensionTotalsTooltipText(key) }}</span>
                  </v-tooltip>
                  <field-label
                    v-model="fieldValues[key]"
                    :field="fieldsWithValues[key]"
                    :select-all-text="selectAllText"
                    :unselect-all-text="unselectAllText"
                  >
                    <!-- pass down scoped slots -->
                    <template v-for="(_, slot) of $scopedSlots" v-slot:[slot]="scope">
                      <slot :name="slot" v-bind="scope" />
                    </template>
                  </field-label>
                </div>
              </template>
            </draggable>
          </div>
        </template>
      </div>
    </div>

    <div class="d-flex flex-nowrap gutter">
      <!-- Row fields -->
      <div v-if="showSettings" class="left-col">
        <div class="drag-area border-primary" :class="dragAreaClass">
          <div class="drag-area-title drag-area-title--with-section-checkbox mb-3 d-flex flex-wrap">
            <span class="drag-area-title__label">{{ rowsLabelText }}</span>
            <v-tooltip
              v-if="internal.totalsMasterEnabled && !isColumnValues"
              bottom
            >
              <template #activator="{ on, attrs }">
                <span
                  class="d-inline-flex ml-2 pivot-totals-checkbox-tooltip-activator"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-checkbox
                    v-model="showGrandTotalColumnEnabled"
                    class="section-grand-total-checkbox mt-0 pt-0"
                    hide-details
                    dense
                  />
                </span>
              </template>
              <span>{{ grandTotalColumnSectionTooltip }}</span>
            </v-tooltip>
          </div>
          <draggable
            v-model="internal.rowFieldKeys"
            class="d-flex flex-column align-items-start gutter-sm drag-area-zone"
            group="fields"
            handle=".btn-draggable"
            @start="start"
            @end="end"
          >
            <template v-for="key in internal.rowFieldKeys">
              <div
                v-if="fieldsWithValues[key]"
                :key="key"
                class="field"
                :class="{ 'field-with-totals-toggle': internal.totalsMasterEnabled }"
              >
                <v-tooltip
                  v-if="internal.totalsMasterEnabled"
                  bottom
                >
                  <template #activator="{ on, attrs }">
                    <span
                      class="d-inline-flex pivot-totals-checkbox-tooltip-activator field-totals-checkbox-wrap"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-checkbox
                        v-model="internal.rowFieldTotalsEnabled[key]"
                        class="field-totals-checkbox mt-0 pt-0"
                        hide-details
                        dense
                      />
                    </span>
                  </template>
                  <span>{{ dimensionTotalsTooltipText(key) }}</span>
                </v-tooltip>
                <field-label
                  v-model="fieldValues[key]"
                  :field="fieldsWithValues[key]"
                  :select-all-text="selectAllText"
                  :unselect-all-text="unselectAllText"
                >
                  <!-- pass down scoped slots -->
                  <template v-for="(_, slot) of $scopedSlots" v-slot:[slot]="scope">
                    <slot :name="slot" v-bind="scope" />
                  </template>
                </field-label>
              </div>
            </template>
          </draggable>
        </div>
      </div>

      <!-- Table -->
      <div class="flex-fill" :style="tableWrapperStyle">
        <pivot-table
          :data="data"
          :row-fields="rowFields"
          :col-fields="colFields"
          :reducer="reducer"
          :no-data-warning-text="noDataWarningText"
          :is-data-loading="isDataLoading"
          :value-columns="currentColumns"
          :is-column-values="isColumnValues"
          :row-field-keys-for-totals="rowFieldKeysForTotals"
          :col-field-keys-for-totals="colFieldKeysForTotals"
          :totals-master-enabled="internal.totalsMasterEnabled"
          :grand-total-label="grandTotalLabel"
          :show-grand-total-column-enabled="showGrandTotalColumnEnabled"
          :show-grand-total-row-enabled="showGrandTotalRowEnabled"
          :column-values-footer-totals-enabled="columnValuesFooterTotalsEnabled"
        >
          <!-- pass down scoped slots -->
          <template v-for="(_, slot) of $scopedSlots" v-slot:[slot]="scope">
            <slot :name="slot" v-bind="scope" />
          </template>
        </pivot-table>
      </div>
    </div>
  </div>
</template>

<script>
import Draggable from 'vuedraggable';
import naturalSort from 'javascript-natural-sort';
import FieldLabel from './FieldLabel.vue';
import PivotTable from './PivotTable.vue';

function loadFieldTotalsMap(storageKey, fieldKeys) {
  let stored = {};
  if (typeof localStorage !== 'undefined') {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          stored = parsed;
        }
      }
    } catch {
      stored = {};
    }
  }
  const result = {};
  (fieldKeys || []).forEach((key) => {
    result[key] = Object.prototype.hasOwnProperty.call(stored, key)
      ? Boolean(stored[key])
      : false;
  });
  return result;
}

export default {
  name: 'VuePivot',
  components: { FieldLabel, PivotTable, Draggable },
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    fields: {
      type: Array,
      default: () => [],
    },
    availableFieldKeys: {
      type: Array,
      default: () => [],
    },
    rowFieldKeys: {
      type: Array,
      default: () => [],
    },
    colFieldKeys: {
      type: Array,
      default: () => [],
    },
    reducer: {
      type: Function,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      default: (sum, item) => sum + 1,
    },
    defaultShowSettings: {
      type: Boolean,
      default: true,
    },
    availableFieldsLabelText: {
      type: String,
      default: 'Available fields',
    },
    colsLabelText: {
      type: String,
      default: 'Columns',
    },
    rowsLabelText: {
      type: String,
      default: 'Rows',
    },
    hideSettingsText: {
      type: String,
      default: 'Hide settings',
    },
    showSettingsText: {
      type: String,
      default: 'Show settings',
    },
    noDataWarningText: {
      type: String,
      default: 'No data to display.',
    },
    selectAllText: {
      type: String,
      default: 'Select all',
    },
    unselectAllText: {
      type: String,
      default: 'Unselect all',
    },
    isDataLoading: {
      type: Boolean,
      default: false,
    },
    valueColumns: {
      type: Array,
      default: () => [],
    },
    /** Prepended to field label for native title/aria on dimension totals checkboxes, e.g. "Подводить итоги по ". */
    dimensionTotalsTooltipPrefix: {
      type: String,
      default: 'Подводить итоги по ',
    },
    totalsMasterLabel: {
      type: String,
      default: 'Подводить итоги',
    },
    /** Label used in the table and in section tooltips for «Всего» toggles (classic pivot). */
    grandTotalLabel: {
      type: String,
      default: 'Всего',
    },
  },
  data() {
    const fieldValues = {};
    this.fields.filter((field) => field.valueFilter).forEach((field) => {
      fieldValues[field.key] = {};
    });

    let initialAvailableFieldKeys = this.availableFieldKeys;
    let initialRowFieldKeys = this.rowFieldKeys;
    let initialColFieldKeys = this.colFieldKeys;
    let totalsMasterEnabled = false;
    if (typeof localStorage !== 'undefined' && localStorage) {
      if (localStorage.getItem('table-availableFieldKeys')) {
        initialAvailableFieldKeys = JSON.parse(localStorage.getItem('table-availableFieldKeys'));
      }
      if (localStorage.getItem('table-rowFieldKeys')) {
        initialRowFieldKeys = JSON.parse(localStorage.getItem('table-rowFieldKeys'));
      }
      if (localStorage.getItem('table-colFieldKeys')) {
        initialColFieldKeys = JSON.parse(localStorage.getItem('table-colFieldKeys'));
      }
      if (localStorage.getItem('table-totalsMasterEnabled')) {
        totalsMasterEnabled = !!Number(localStorage.getItem('table-totalsMasterEnabled'));
      }
    }

    let showGrandTotalColumnEnabled = true;
    let showGrandTotalRowEnabled = true;
    let columnValuesFooterTotalsEnabled = true;
    if (typeof localStorage !== 'undefined' && localStorage) {
      const storedColGt = localStorage.getItem('table-showGrandTotalColumnEnabled');
      if (storedColGt !== null) {
        showGrandTotalColumnEnabled = !!Number(storedColGt);
      }
      const storedRowGt = localStorage.getItem('table-showGrandTotalRowEnabled');
      if (storedRowGt !== null) {
        showGrandTotalRowEnabled = !!Number(storedRowGt);
      }
      const storedCvFooter = localStorage.getItem('table-columnValuesFooterTotalsEnabled');
      if (storedCvFooter !== null) {
        columnValuesFooterTotalsEnabled = !!Number(storedCvFooter);
      }
    }

    return {
      internal: {
        availableFieldKeys: initialAvailableFieldKeys,
        rowFieldKeys: initialRowFieldKeys,
        colFieldKeys: initialColFieldKeys,
        oldColFieldKeys: localStorage.getItem('table-oldColFieldKeys') ? JSON.parse(localStorage.getItem('table-oldColFieldKeys')) : [],
        rowFieldTotalsEnabled: loadFieldTotalsMap('table-rowFieldTotals', initialRowFieldKeys),
        colFieldTotalsEnabled: loadFieldTotalsMap('table-colFieldTotals', initialColFieldKeys),
        totalsMasterEnabled,
      },
      fieldValues,
      dragging: false,
      showSettings: true,
      isColumnValues: localStorage.getItem('table-isColumnValues') ? !!Number(localStorage.getItem('table-isColumnValues')) : false,
      currentKeyColumns: localStorage.getItem('table-currentKeyColumns') ? JSON.parse(localStorage.getItem('table-currentKeyColumns')) : [],
      showGrandTotalColumnEnabled,
      showGrandTotalRowEnabled,
      columnValuesFooterTotalsEnabled,
    };
  },
  computed: {
    currentColumns() {
      const array = [];
      this.currentKeyColumns.forEach((key) => {
        const item = this.valueColumns.find((i) => i.key === key);
        if (item) {
          array.push(item);
        }
      });
      return array;
    },
    // Fields with values extracted from data (if field has valueFilter)
    fieldsWithValues() {
      // Create object: field.key => field
      const fieldsWithValues = {};

      this.fields.forEach((field) => {
        fieldsWithValues[field.key] = field;
      });

      // Add valuesSet
      const valueFilterableFields = this.fields.filter((field) => field.valueFilter);

      // Create valuesSet for each value filterable field
      valueFilterableFields.forEach((field) => {
        fieldsWithValues[field.key].valuesSet = new Set();
      });

      // Iterate on data once
      this.data.forEach((item) => {
        valueFilterableFields.forEach((field) => {
          fieldsWithValues[field.key].valuesSet.add(field.getter(item));
        });
      });

      // Creates values sorted from valuesSet
      valueFilterableFields.forEach((field) => {
        fieldsWithValues[field.key].values = Array.from(fieldsWithValues[field.key].valuesSet).sort(field.sort || naturalSort);
      });

      return fieldsWithValues;
    },
    // Fields selected values as set
    valuesFiltered() {
      const valuesFiltered = {};

      // eslint-disable-next-line no-restricted-syntax
      for (const [key, valuesObject] of Object.entries(this.fieldValues)) {
        valuesFiltered[key] = new Set();
        valuesObject.forEach((valueObject) => {
          if (valueObject.checked) {
            valuesFiltered[key].add(valueObject.value);
          }
        });
      }

      return valuesFiltered;
    },
    // Pivot table props from Pivot props & data
    rowFields() {
      const rowFields = [];

      this.internal.rowFieldKeys.forEach((key) => {
        // eslint-disable-next-line no-shadow
        const field = this.fields.find((field) => field.key === key);
        if (!field) {
          return;
        }

        // Generate headerSlotNames from headers
        if (field.headers) {
          field.headerSlotNames = field.headers
            .filter((header) => header.checked)
            .map((header) => header.slotName);
        }

        // Add selected values
        if (field.valueFilter) {
          field.valuesFiltered = this.valuesFiltered[field.key];
        }

        rowFields.push(field);
      });

      return rowFields;
    },
    colFields() {
      const colFields = [];

      if (!this.isColumnValues) {
        this.internal.colFieldKeys.forEach((key) => {
          // eslint-disable-next-line no-shadow
          const field = this.fields.find((field) => field.key === key);
          if (!field) {
            return;
          }

          // Generate headerSlotNames from headers
          if (field.headers) {
            field.headerSlotNames = field.headers
              .filter((header) => header.checked)
              .map((header) => header.slotName);
          }

          // Add selected values
          if (field.valueFilter) {
            field.valuesFiltered = this.valuesFiltered[field.key];
          }

          colFields.push(field);
        });
      }

      return colFields;
    },
    rowFieldKeysForTotals() {
      return this.internal.rowFieldKeys.filter(
        (key) => this.internal.rowFieldTotalsEnabled[key],
      );
    },
    colFieldKeysForTotals() {
      return this.internal.colFieldKeys.filter(
        (key) => this.internal.colFieldTotalsEnabled[key],
      );
    },
    // Drag area class
    dragAreaClass() {
      return this.dragging ? 'drag-area-highlight' : null;
    },
    // Table wrapper style
    tableWrapperStyle() {
      const maxWidth = this.showSettings ? 'calc(100% - 200px - 2rem)' : '100%';
      return `max-width: ${maxWidth};`;
    },
    grandTotalColumnSectionTooltip() {
      return `Показывать колонку «${this.grandTotalLabel}»`;
    },
    grandTotalRowSectionTooltip() {
      return `Показывать строку «${this.grandTotalLabel}»`;
    },
    columnValuesFooterTotalsTooltip() {
      return `Показывать строку «${this.grandTotalLabel}» внизу таблицы`;
    },
  },
  watch: {
    data() {
      this.updateFieldValues();
    },
    'internal.availableFieldKeys': function (value) {
      if (typeof window !== 'undefined' && localStorage) {
        localStorage.setItem('table-availableFieldKeys', JSON.stringify(value));
      }
    },
    'internal.rowFieldKeys': function (value) {
      if (typeof window !== 'undefined' && localStorage) {
        localStorage.setItem('table-rowFieldKeys', JSON.stringify(value));
      }
      this.syncRowFieldTotalsMap(value);
    },
    'internal.colFieldKeys': function (value) {
      if (typeof window !== 'undefined' && localStorage) {
        localStorage.setItem('table-colFieldKeys', JSON.stringify(value));
      }
      this.syncColFieldTotalsMap(value);
    },
    'internal.rowFieldTotalsEnabled': {
      deep: true,
      handler(value) {
        if (typeof window !== 'undefined' && localStorage) {
          localStorage.setItem('table-rowFieldTotals', JSON.stringify(value));
        }
      },
    },
    'internal.colFieldTotalsEnabled': {
      deep: true,
      handler(value) {
        if (typeof window !== 'undefined' && localStorage) {
          localStorage.setItem('table-colFieldTotals', JSON.stringify(value));
        }
      },
    },
    'internal.totalsMasterEnabled': function (value) {
      if (typeof window !== 'undefined' && localStorage) {
        localStorage.setItem('table-totalsMasterEnabled', Number(value));
      }
    },
    showGrandTotalColumnEnabled(value) {
      if (typeof window !== 'undefined' && localStorage) {
        localStorage.setItem('table-showGrandTotalColumnEnabled', Number(value));
      }
    },
    showGrandTotalRowEnabled(value) {
      if (typeof window !== 'undefined' && localStorage) {
        localStorage.setItem('table-showGrandTotalRowEnabled', Number(value));
      }
    },
    columnValuesFooterTotalsEnabled(value) {
      if (typeof window !== 'undefined' && localStorage) {
        localStorage.setItem('table-columnValuesFooterTotalsEnabled', Number(value));
      }
    },
    'internal.oldColFieldKeys': function (value) {
      if (typeof window !== 'undefined' && localStorage) {
        localStorage.setItem('table-oldColFieldKeys', JSON.stringify(value));
      }
    },
    currentKeyColumns(value) {
      if (typeof window !== 'undefined' && localStorage) {
        localStorage.setItem('table-currentKeyColumns', JSON.stringify(value));
      }
    },
    isColumnValues: {
      immediate: true,
      handler(value, oldValue) {
        this.$emit('selectedIsColumnValues', value);
        if (typeof window !== 'undefined' && localStorage) {
          localStorage.setItem('table-isColumnValues', Number(value));
          if (oldValue !== undefined) {
            if (value) {
              this.internal.oldColFieldKeys = this.internal.colFieldKeys;
              this.internal.availableFieldKeys = [...this.internal.availableFieldKeys, ...this.internal.colFieldKeys];
              this.internal.colFieldKeys = [];
            } else {
              this.internal.colFieldKeys = this.internal.oldColFieldKeys;
              this.internal.availableFieldKeys = [...this.internal.availableFieldKeys.filter((field) => !this.internal.colFieldKeys.includes(field))];
              this.internal.oldColFieldKeys = [];
            }
          }
        }
      },
    },
  },
  created() {
    this.showSettings = this.defaultShowSettings;
    this.pruneStaleFieldKeys();
    this.updateFieldValues();
  },
  methods: {
    /** Drop keys from localStorage that no longer exist in `fields` (avoids undefined `field` in UI). */
    pruneStaleFieldKeys() {
      const valid = new Set(this.fields.map((f) => f.key));
      const prune = (arr) => (arr || []).filter((k) => valid.has(k));
      this.internal.availableFieldKeys = prune(this.internal.availableFieldKeys);
      this.internal.rowFieldKeys = prune(this.internal.rowFieldKeys);
      if (!this.isColumnValues) {
        this.internal.colFieldKeys = prune(this.internal.colFieldKeys);
      }
      this.internal.oldColFieldKeys = prune(this.internal.oldColFieldKeys);
      this.syncRowFieldTotalsMap(this.internal.rowFieldKeys);
      this.syncColFieldTotalsMap(this.internal.colFieldKeys);
    },
    // Toggle settings
    toggleShowSettings() {
      this.showSettings = !this.showSettings;
    },
    // Update dragging boolean
    start() {
      this.dragging = true;
    },
    end() {
      this.dragging = false;
    },
    // Update fieldValues
    updateFieldValues() {
      // eslint-disable-next-line no-restricted-syntax
      for (const [key, field] of Object.entries(this.fieldsWithValues)) {
        if (field.valueFilter) {
          this.fieldValues[key] = [];
          field.values.forEach((value) => {
            this.fieldValues[key].push({ value, checked: true });
          });
        }
      }
    },
    syncRowFieldTotalsMap(keys) {
      const prev = { ...this.internal.rowFieldTotalsEnabled };
      const next = {};
      (keys || []).forEach((key) => {
        next[key] = Object.prototype.hasOwnProperty.call(prev, key) ? prev[key] : false;
      });
      this.internal.rowFieldTotalsEnabled = next;
    },
    syncColFieldTotalsMap(keys) {
      if (this.isColumnValues) {
        return;
      }
      const prev = { ...this.internal.colFieldTotalsEnabled };
      const next = {};
      (keys || []).forEach((key) => {
        next[key] = Object.prototype.hasOwnProperty.call(prev, key) ? prev[key] : false;
      });
      this.internal.colFieldTotalsEnabled = next;
    },
    dimensionTotalsTooltipText(fieldKey) {
      const field = this.fields.find((f) => f.key === fieldKey);
      const label = field && field.label != null ? field.label : fieldKey;
      return `${this.dimensionTotalsTooltipPrefix}${label}`;
    },
  },
};
</script>

<style lang="scss" scoped>
/* Left column width */
.left-col {
  min-width: 200px;
  max-width: 200px;
}

/* Grid with gutter */
.gutter, .gutter-y {
  margin-top: -1rem;

  > * {
    margin-top: 1rem;
  }
}
.gutter-x, .gutter {
  margin-left: -1rem;
  > * {
    margin-left: 1rem;
  }
}

.gutter-sm, .gutter-y-sm {
  margin-top: -.5rem;

  > * {
    margin-top: .5rem;
  }
}
.gutter-x-sm, .gutter-sm {
  margin-left: -.5rem;
  > * {
    margin-left: .5rem;
  }
}

/* Drag & drop stuff */
.drag-area {
  border: 1px dashed #ccc;
  padding: 0.75rem;
  transition: background-color 0.4s;

  .drag-area-title {
    line-height: 1;
  }

  /* Align section «Строки» / «Колонки» grand-total checkbox with label cap height (Vuetify adds v-input top margin). */
  .drag-area-title--with-section-checkbox {
    align-items: center;

    .drag-area-title__label {
      line-height: 1.2;
    }
  }

  .drag-area-zone {
    min-width: 10rem;
    min-height: 46px;
  }
}

.drag-area-highlight {
  background-color: #f3f3f3;
}

.sortable-ghost {
  opacity: 0.4;
}

.field {
  position: relative;
}

.field-with-totals-toggle {
  display: flex;
  align-items: flex-start;
  flex-wrap: nowrap;
}

.field-totals-checkbox {
  flex-shrink: 0;
}

/* Wrapper for v-tooltip activator — keeps flex layout next to field labels. */
.field-totals-checkbox-wrap {
  flex-shrink: 0;
  margin-right: 0.25rem;
  align-items: flex-start;
}

.pivot-totals-checkbox-tooltip-activator {
  align-items: flex-start;
  cursor: default;
}

.section-grand-total-checkbox {
  flex-shrink: 0;
  transform: translateY(-0.08em);

  ::v-deep .v-input {
    margin-top: 0 !important;
    padding-top: 0;
  }

  ::v-deep .v-input--selection-controls {
    margin-top: 0 !important;
    padding-top: 0;
  }

  ::v-deep .v-input__slot {
    margin-bottom: 0 !important;
  }
}

.column-values-select-row {
  gap: 0.25rem;
}

.column-values-select {
  min-width: 200px;
}
</style>
